import React, { useEffect, useMemo, useState } from 'react';
import { Box, Container, Tab, Tabs, Typography } from '@mui/material';
import { Link as RouterLink, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import api from '../config/api';
import {
  DOCUMENT,
  PAGE_CONTAINER_MAX_WIDTH,
  documentPageCanvasSx,
  documentPageContainerSx,
  documentSubTabsSx,
} from '../constants/documentTheme';

export const ADMIN_NAV = [
  { key: 'overview', to: '/admin', end: true, labelKey: 'adminNav.overview' },
  { key: 'features', to: '/admin/features', labelKey: 'adminNav.features' },
  { key: 'private', to: '/admin/private', labelKey: 'adminNav.private', feature: 'privateTravel' },
  { key: 'campaigns', to: '/admin/campaigns', labelKey: 'adminNav.campaigns' },
  { key: 'shows', to: '/admin/marketing-shows', labelKey: 'adminNav.shows' },
  { key: 'applications', to: '/admin/partner-applications', labelKey: 'adminNav.applications' },
];

function adminNavValue(pathname) {
  if (pathname.startsWith('/admin/features')) return '/admin/features';
  if (pathname.startsWith('/admin/private')) return '/admin/private';
  if (pathname.startsWith('/admin/campaigns')) return '/admin/campaigns';
  if (pathname.startsWith('/admin/marketing-shows')) return '/admin/marketing-shows';
  if (pathname.startsWith('/admin/partner-applications')) return '/admin/partner-applications';
  return '/admin';
}

/**
 * Shared Document-theme chrome for /admin pages: canvas, nav rail, page header.
 * Product surfaces (e.g. Private Travel) appear in nav only when enabled under
 * Admin → Features. Env kill-switches can still force a feature unavailable.
 */
const AdminChrome = ({
  title,
  subtitle,
  actions,
  children,
  maxWidth = PAGE_CONTAINER_MAX_WIDTH,
}) => {
  const { t } = useTranslation();
  const { pathname } = useLocation();
  const current = adminNavValue(pathname);
  const onPrivateRoute = pathname.startsWith('/admin/private');
  const [privateTravelEnabled, setPrivateTravelEnabled] = useState(false);

  useEffect(() => {
    let cancelled = false;
    api.get('/admin/features')
      .then(({ data }) => {
        if (cancelled) return;
        const privateTravel = (data.features || []).find((f) => f.key === 'privateTravel');
        setPrivateTravelEnabled(Boolean(privateTravel?.enabled));
      })
      .catch(() => {
        if (!cancelled) setPrivateTravelEnabled(false);
      });
    return () => {
      cancelled = true;
    };
  }, [pathname]);

  const navItems = useMemo(
    () => ADMIN_NAV.filter((item) => {
      if (item.feature === 'privateTravel') {
        // Keep tab while viewing Private so deep links do not strand admins
        return privateTravelEnabled || onPrivateRoute;
      }
      return true;
    }),
    [privateTravelEnabled, onPrivateRoute]
  );

  const tabValue = navItems.some((item) => item.to === current) ? current : '/admin';

  return (
    <Box sx={{ ...documentPageCanvasSx, minHeight: '100vh', pb: 8 }}>
      <Box
        sx={{
          borderBottom: `1px solid ${DOCUMENT.rule}`,
          bgcolor: DOCUMENT.surface,
        }}
      >
        <Container maxWidth={maxWidth} sx={documentPageContainerSx}>
          <Box
            sx={{
              display: 'flex',
              alignItems: { xs: 'flex-start', md: 'flex-end' },
              justifyContent: 'space-between',
              flexWrap: 'wrap',
              gap: 2,
              pt: { xs: 3, md: 4 },
              pb: 0,
            }}
          >
            <Box sx={{ minWidth: 0, flex: 1 }}>
              <Typography
                sx={{
                  fontFamily: DOCUMENT.sans,
                  fontSize: 11,
                  letterSpacing: '0.14em',
                  textTransform: 'uppercase',
                  color: DOCUMENT.accent,
                  mb: 1,
                }}
              >
                {t('adminNav.eyebrow')}
              </Typography>
              <Typography
                component="h1"
                sx={{
                  fontFamily: DOCUMENT.display,
                  fontWeight: 600,
                  fontSize: { xs: '1.75rem', md: '2.15rem' },
                  color: DOCUMENT.ink,
                  lineHeight: 1.15,
                  mb: subtitle ? 1 : 0,
                }}
              >
                {title}
              </Typography>
              {subtitle ? (
                <Typography
                  sx={{
                    color: DOCUMENT.ink3,
                    fontSize: '0.95rem',
                    maxWidth: 560,
                    mb: 0.5,
                  }}
                >
                  {subtitle}
                </Typography>
              ) : null}
            </Box>
            {actions ? (
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', pb: 1 }}>
                {actions}
              </Box>
            ) : null}
          </Box>

          <Tabs
            value={tabValue}
            variant="scrollable"
            scrollButtons="auto"
            allowScrollButtonsMobile
            sx={{
              ...documentSubTabsSx,
              mt: 2,
              borderBottom: 'none',
              minHeight: 48,
            }}
          >
            {navItems.map((item) => (
              <Tab
                key={item.key}
                label={t(item.labelKey)}
                value={item.to}
                component={RouterLink}
                to={item.to}
                disableRipple
              />
            ))}
          </Tabs>
        </Container>
        <Box sx={{ borderBottom: `1px solid ${DOCUMENT.rule}` }} />
      </Box>

      <Container maxWidth={maxWidth} sx={{ ...documentPageContainerSx, pt: { xs: 3, md: 4 } }}>
        {children}
      </Container>
    </Box>
  );
};

export const adminSurfaceCardSx = {
  bgcolor: DOCUMENT.surface,
  border: `1px solid ${DOCUMENT.ruleHard}`,
  borderRadius: 0,
  boxShadow: 'none',
};

export const adminStatPaperSx = {
  p: 2.5,
  height: '100%',
  bgcolor: DOCUMENT.surface,
  border: `1px solid ${DOCUMENT.ruleHard}`,
  borderRadius: 0,
  boxShadow: 'none',
};

export const adminSectionTitleSx = {
  fontFamily: DOCUMENT.display,
  fontWeight: 600,
  fontSize: '1.35rem',
  color: DOCUMENT.ink,
  mb: 0.5,
};

export const adminOutlineBtnSx = {
  background: 'transparent',
  color: DOCUMENT.oxblood,
  border: `1px solid ${DOCUMENT.oxblood}`,
  borderRadius: 0,
  fontFamily: DOCUMENT.sans,
  fontSize: 11,
  letterSpacing: '0.11em',
  textTransform: 'uppercase',
  px: 2.5,
  py: 1.25,
  boxShadow: 'none',
  '&:hover': {
    background: `color-mix(in srgb, ${DOCUMENT.oxblood} 8%, transparent)`,
    borderColor: DOCUMENT.oxblood,
    boxShadow: 'none',
  },
};

export const adminSolidBtnSx = {
  background: DOCUMENT.oxblood,
  color: DOCUMENT.ivory,
  border: 0,
  borderRadius: 0,
  fontFamily: DOCUMENT.sans,
  fontSize: 11,
  letterSpacing: '0.11em',
  textTransform: 'uppercase',
  px: 2.5,
  py: 1.25,
  boxShadow: 'none',
  '&:hover': {
    background: DOCUMENT.nav,
    boxShadow: 'none',
  },
  '&.Mui-disabled': {
    background: DOCUMENT.ruleHard,
    color: DOCUMENT.ink3,
  },
};

export default AdminChrome;
